import React from 'react';

// helpers
import useTranslation from 'hooks/useTranslation';
import { styled } from 'styled-components';
import { useField } from 'formik';
import { colorsTheme } from 'resources/theme/styled/colors';
import { IndividualFormItem } from '.';
import { INTAKE_FORM_FIELDS_GRID_SIZES } from 'constants/grids';
import { getPlaceholderForInputField, getPlaceholderForSelect } from '../utils';

// components
import FieldIssue from '../FieldIssue';
import HideIfDisabledForm from 'components/Forms/HideIfDisabledForm';
import CountriesAutocomplete, {
  IProps as CountriesAutocompleteProps,
} from 'components/Forms/FormComponents/Autocompletes/CRM/CountriesAutocomplete';
import {
  Button,
  Card,
  Col,
  FormField,
  FormInput,
  FormInputProps,
  FormInputTextArea,
  FormInputTextAreaProps,
  Row,
  SectionIntro,
} from '@ui';

interface IProps {
  index: number;
  fieldName: string;
  deleteCallback?: () => void;
}

const IndividualForm = ({ index, fieldName, deleteCallback }: IProps) => {
  const { t } = useTranslation('onboarding');
  const [field] = useField<IndividualFormItem>(fieldName);

  return (
    <StyledCard>
      <SectionIntro
        hideDivider
        titleColor={colorsTheme.colorWhite}
        titleVariant="h5"
        title={t('intake_form.associated_individuals.individual_title', {
          index: index + 1,
        })}
      />
      <Row wrap={true} justify="start">
        <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
          <FormField<FormInputProps>
            label={
              <FieldIssue
                fieldId={`beneficialOwners["${field.value.id}"].fullName`}
              >
                {t('intake_form.associated_individuals.full_name')}
              </FieldIssue>
            }
            name={`${fieldName}.fullName`}
            component={FormInput}
            additionalProps={{
              renderPlaceholder: (validationRules) =>
                getPlaceholderForInputField(validationRules, true, t),
            }}
          />
        </Col>
        <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
          <FormField<CountriesAutocompleteProps>
            label={
              <FieldIssue
                fieldId={`beneficialOwners["${field.value.id}"].countryOfResidence`}
              >
                {t('intake_form.associated_individuals.country')}
              </FieldIssue>
            }
            name={`${fieldName}.country`}
            component={CountriesAutocomplete}
            additionalProps={{
              initialValue: field.value.countryInitialOption,
              renderPlaceholder: (validationRules) =>
                getPlaceholderForSelect(validationRules, true, false, t),
            }}
          />
        </Col>
        <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
          <FormField<FormInputTextAreaProps>
            label={
              <FieldIssue
                fieldId={`beneficialOwners["${field.value.id}"].relationshipToEntity`}
              >
                {t('intake_form.associated_individuals.relationship')}
              </FieldIssue>
            }
            name={`${fieldName}.relationship`}
            component={FormInputTextArea}
            additionalProps={{
              rows: 5,
              renderPlaceholder: (validationRules) =>
                getPlaceholderForInputField(validationRules, true, t),
            }}
          />
        </Col>
      </Row>
      {deleteCallback ? (
        <HideIfDisabledForm hideIfSubmitting>
          <Button size="large" onClick={deleteCallback}>
            {t('remove', { ns: 'common' })}
          </Button>
        </HideIfDisabledForm>
      ) : null}
    </StyledCard>
  );
};

const StyledCard = styled(Card)`
  .ant-card-body {
    min-height: 0px;
    padding: ${({ theme }) => theme.paddingXl};
  }
`;

export default IndividualForm;
